import React, {useCallback, useState} from 'react';
import {Button} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconPlus} from '@tabler/icons-react';
import type {
  WorkflowDefinitionActionResponse} from 'src/api/ProactApiClient';
import {
  TaskTypeEnum,
  WorkflowDefinitionActionTypeEnum,
} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import type {
  IClientKeyed} from 'src/app/workflows/clientIdentity';
import {
  itemKey,
  nextClientKey,
} from 'src/app/workflows/clientIdentity';
import {GetWorkflowActionLabel} from 'src/app/workflows/workflowHelpers';
import type {IWorkflowDesignerMetadata} from 'src/app/workflows/types';
import {ActionCard} from './ActionCard';

export interface ActionListProps {
  actions: WorkflowDefinitionActionResponse[];
  onActionsChange: (actions: WorkflowDefinitionActionResponse[]) => void;
  metadata: IWorkflowDesignerMetadata;
}

export const ActionList: React.FC<ActionListProps> = ({
  actions,
  onActionsChange,
  metadata,
}) => {
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [expandedSet, setExpandedSet] = useState<Set<string | number>>(
    () => new Set<string | number>()
  );

  const toggleExpanded = useCallback((key: string | number) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedSet(new Set(actions.map((action, i) => itemKey(action, i))));
  }, [actions]);

  const collapseAll = useCallback(() => {
    setExpandedSet(new Set());
  }, []);

  const handleAddNew = () => {
    const clientKey = nextClientKey();
    onActionsChange([
      ...actions,
      {
        clientKey,
        type: WorkflowDefinitionActionTypeEnum.Email,
        metadata: {
          emailMetadata: {},
          assignTaskMetadata: {},
          createTaskMetadata: {
            taskType: TaskTypeEnum.Actions,
          },
        },
      } as WorkflowDefinitionActionResponse & IClientKeyed,
    ]);
    setExpandedSet(prev => new Set(prev).add(clientKey));
  };

  const handleActionChange = (
    action: WorkflowDefinitionActionResponse,
    index: number
  ) => {
    const newActions = Array.from(actions);
    newActions[index] = action;
    onActionsChange(newActions);
  };

  const handleDeleteRequest = (index: number) => {
    setDeleteIndex(index);
  };

  const handleDeleteConfirm = () => {
    if (deleteIndex !== null) {
      onActionsChange([
        ...actions.slice(0, deleteIndex),
        ...actions.slice(deleteIndex + 1),
      ]);
      setDeleteIndex(null);
    }
  };

  const deleteActionLabel =
    deleteIndex !== null
      ? GetWorkflowActionLabel(actions[deleteIndex]?.type)
      : '';

  return (
    <Stack gap="md">
      {actions.length > 1 && (
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" size="xs" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="subtle" size="xs" onClick={collapseAll}>
            Collapse All
          </Button>
        </Group>
      )}

      {actions.length === 0 ? (
        <Alert variant="light" color="gray" title="No actions configured">
          <Text size="sm" mb="sm">
            Actions define what happens during this phase. Add at least one
            action.
          </Text>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAddNew}
            size="sm">
            Add First Action
          </Button>
        </Alert>
      ) : (
        <>
          {actions.map((action, index) => {
            const key = itemKey(action, index);
            return (
              <ActionCard
                key={key}
                action={action}
                index={index}
                expanded={expandedSet.has(key)}
                onToggleExpanded={() => toggleExpanded(key)}
                onActionChange={handleActionChange}
                onDeleteRequest={handleDeleteRequest}
                metadata={metadata}
              />
            );
          })}
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAddNew}
            size="sm"
            w="fit-content">
            Add Action
          </Button>
        </>
      )}

      <ConfirmDialog
        opened={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        onConfirm={handleDeleteConfirm}
        title={`Delete Action ${deleteIndex !== null ? deleteIndex + 1 : ''}?`}
        body={`Are you sure you want to delete the "${deleteActionLabel}" action? This cannot be undone.`}
        confirmationButtonColor="red"
        buttonTextYes="Delete"
        buttonTextNo="Cancel"
      />
    </Stack>
  );
};
